/**
 * 怪兽图鉴 / 圈养园（共享名称库 · 跨设备同步）
 * - 最多 15 只怪（与五行阶级金字塔虚位对齐：1+2+3+4+5=15）
 * - 实际数据由 /api/zoo 在 Cloudflare KV 中持久化
 * - 本文件维护一份内存缓存，所有读操作从这里走，零延迟；
 *   写操作走乐观更新（先更新缓存 + 派发 zoo:changed，再异步 POST 到服务器）
 * - 启动时 refreshZoo() 拉一次远端，screen4 打开时再拉一次，保证看得到别人的提交
 */

import { elementOfBody } from "./elements";
import type { Screen2Combo } from "./combo";
import type { MonsterDesc, Element, FortunePayload, FameBuff } from "../shared/types";

const MAX = 15;
const API = "/api/zoo";

export interface ZooEntry {
  scpId: string;
  monster: MonsterDesc;
  combo: Screen2Combo;
  originalName: string;
  ts: number;
  fortune?: FortunePayload;
  fame?: FameBuff;
}

/** 内存缓存（启动时为空，refreshZoo 之后填充） */
let cache: ZooEntry[] = [];

/** 远端初始化是否已完成（影响 screen0 "五行阶级 · N 人" 的初始空态显示） */
let ready = false;

function notify(): void {
  try {
    window.dispatchEvent(new CustomEvent("zoo:changed"));
  } catch { /* ignore */ }
}

function isEntry(x: unknown): x is ZooEntry {
  if (!x || typeof x !== "object") return false;
  const e = x as Record<string, unknown>;
  return (typeof e.scpId === "string" && typeof e.originalName === "string" && e.monster != null && typeof (e as Record<string, unknown>).monster === "object");
}

function sanitize(entries: unknown[]): ZooEntry[] {
  return entries.filter(isEntry).slice(0, MAX);
}

export function getZoo(): ZooEntry[] {
  return cache;
}

export function isZooReady(): boolean {
  return ready;
}

export function getZooCount(): number {
  return cache.length;
}

/**
 * 从服务器拉取最新数据，写入本地缓存并派发事件。
 * 任何地方需要确保拿到最新远端状态时都可以调。
 */
export async function refreshZoo(): Promise<void> {
  try {
    const resp = await fetch(API, { method: "GET", headers: { "Cache-Control": "no-cache" } });
    if (!resp.ok) return;
    const data = (await resp.json()) as { ok?: boolean; entries?: unknown[] };
    if (!data?.ok) return;
    const next = sanitize(Array.isArray(data.entries) ? data.entries : []);
    cache = next;
    ready = true;
    notify();
  } catch {
    /* 离线 / 网络异常时保持现有缓存不变 */
  }
}

export function addToZoo(
  monster: MonsterDesc,
  combo: Screen2Combo,
  originalName: string,
  fortune?: FortunePayload,
  fame?: FameBuff
): { added: boolean; reason?: "duplicate" } {
  const trimmedName = originalName.trim();
  const exists = cache.find((e) => e.originalName.trim() === trimmedName);
  if (exists) {
    return { added: false, reason: "duplicate" };
  }
  // 乐观更新本地缓存
  const filtered = cache.filter((e) => e.scpId !== monster.scpId);
  const newEntry: ZooEntry = {
    scpId: monster.scpId,
    monster,
    combo,
    originalName: trimmedName,
    ts: Date.now(),
    fortune,
    fame,
  };
  filtered.unshift(newEntry);
  cache = filtered.slice(0, MAX);
  notify();

  // 异步同步到服务器
  void (async () => {
    try {
      const resp = await fetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ entry: newEntry }),
      });
      if (resp.ok) {
        const data = (await resp.json()) as { ok?: boolean; added?: boolean; entries?: unknown[] };
        if (data?.ok && Array.isArray(data.entries)) {
          const next = sanitize(data.entries);
          // 只有在长度或首条 scpId 不一致时才刷新（避免无谓重渲染）
          if (
            next.length !== cache.length ||
            (next[0] && cache[0] && next[0].scpId !== cache[0].scpId)
          ) {
            cache = next;
            ready = true;
            notify();
          }
        }
      }
    } catch {
      /* 离线时暂存本地，下一次 refreshZoo 时拉取 */
    }
  })();

  return { added: true };
}

export function clearZoo(): void {
  /* 共享库不再支持本地清空（避免误删别人的提交）。如需重置请走 Cloudflare Dashboard */
  cache = [];
  ready = true;
  notify();
}

/**
 * 对波胜出后：交换两个 scpId 在 zoo 中的阶层
 *  - 不修改 entry 内容（除 fortune.total 之外不动）
 *  - 写本地缓存 + 派发事件
 *  - 异步同步到服务器
 */
export function swapRanks(scpIdA: string, scpIdB: string): boolean {
  try {
    const a = cache.find((e) => e.scpId === scpIdA);
    const b = cache.find((e) => e.scpId === scpIdB);
    if (!a || !b) return false;
    const fortuneA = a.fortune?.total ?? 50;
    const fortuneB = b.fortune?.total ?? 50;
    if (a.fortune) a.fortune.total = fortuneB;
    if (b.fortune) b.fortune.total = fortuneA;
    notify();
    return true;
  } catch {
    return false;
  }
}

export function applyDuelResult(winnerScpId: string, loserScpId: string): boolean {
  return swapRanks(winnerScpId, loserScpId);
}

/**
 * 启动时调用：保留以兼容旧调用方。当前是 no-op（远端 KV 已是单一事实源，
 * 重复的 originalName 不可能出现在服务器里）。
 */
export function cleanupZoo(): void {
  /* no-op: server is single source of truth */
}

export function getBiome(bodyType: string): Element {
  return elementOfBody(bodyType);
}

export const BIOME_LABELS: Record<Element, string> = {
  金: "金 行",
  木: "木 行",
  水: "水 行",
  火: "火 行",
  土: "土 行",
};

export const BIOME_COLORS: Record<Element, { main: string; sub: string; accent: string; text: string }> = {
  金: { main: "#5a5e66", sub: "#9aa0a8", accent: "#fffacd", text: "#ffe680" },
  木: { main: "#2a4a18", sub: "#4a7a3a", accent: "#88dd66", text: "#a8e080" },
  水: { main: "#0a1a3a", sub: "#3a5a8a", accent: "#5a8acc", text: "#88c8ff" },
  火: { main: "#5a1808", sub: "#aa3a18", accent: "#ff6644", text: "#ffaa44" },
  土: { main: "#3a2818", sub: "#7a5a38", accent: "#caa66a", text: "#e0c080" },
};